import {
  TrainCustomEventSeverityEnum,
  TrickFormHookGroupedTrainFormSchema,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { FormFieldWithCustomInput } from "features/CenterContent/shared/operationalInformation/components/FormFieldWithCustomInput";
import {
  EventType,
  GenericType,
} from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { FC, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getTrainCustomInputs } from "shared/types/form";

export type BatchCommonInputProps = {
  pathPrefix: `\${groupKey}`;
  numberOfGroups: number;
};

type CustomInputProps = {
  items: GenericType[] | EventType[];
  formFieldType: "event" | "reason" | "action";
  disabled?: boolean;
};

export const BatchTrainFormFieldWithCustomInput: FC<
  CustomInputProps & BatchCommonInputProps
> = ({ pathPrefix, numberOfGroups, items, formFieldType, disabled }) => {
  const { t } = useTranslation();

  const customInputs = getTrainCustomInputs(t)[formFieldType];
  const customInputsWithOptional = {
    severityFormField: undefined,
    ...customInputs,
  };
  const { label, typeFormField, customFormField, severityFormField } =
    customInputsWithOptional;

  const { control, setValue } =
    useFormContext<TrickFormHookGroupedTrainFormSchema>();

  // All groups in a train series have the same value for these fields,
  // Use the values from the first group to display in modal
  const displayTypeFormField = `${pathPrefix}.0.${typeFormField}` as const;
  const displayCustomFormField = `${pathPrefix}.0.${customFormField}` as const;
  const displaySeverityFormField = severityFormField
    ? (`${pathPrefix}.0.${severityFormField}` as const)
    : undefined;

  const displayTypeForm = useWatch({ control, name: displayTypeFormField });
  const displayCustomForm = useWatch({ control, name: displayCustomFormField });
  const displaySeverityFormRaw = useWatch({
    control,
    name: displaySeverityFormField ?? displayTypeFormField,
  });
  const displaySeverityForm = displaySeverityFormField
    ? (displaySeverityFormRaw as TrainCustomEventSeverityEnum | undefined)
    : undefined;

  // React only reruns the effect below when a dependency's value actually looks
  // different. displayCustomForm is an object, and React can't tell its content
  // changed just by comparing it - we stringify it so React sees a new value
  // every time the text changes.
  const displayCustomFormKey = JSON.stringify(displayCustomForm);

  useEffect(() => {
    // Update the other groups if typeForm or customForm changes
    for (let n = 1; n < numberOfGroups; n += 1) {
      const groupTypeFormField = `${pathPrefix}.${n}.${typeFormField}` as const;
      const groupCustomFormField =
        `${pathPrefix}.${n}.${customFormField}` as const;
      setValue(groupTypeFormField, displayTypeForm);
      setValue(groupCustomFormField, displayCustomForm);
      if (severityFormField && displaySeverityForm) {
        const groupSeverityFormField =
          `${pathPrefix}.${n}.${severityFormField}` as const;
        setValue(groupSeverityFormField, displaySeverityForm);
      }
    }
  }, [displayTypeForm, displayCustomFormKey, displaySeverityForm]);

  return (
    <FormFieldWithCustomInput
      items={items}
      disabled={disabled}
      label={label}
      typeFormField={displayTypeFormField}
      customFormField={displayCustomFormField}
      severityFormField={displaySeverityFormField}
    />
  );
};
